/**
 * Stream session lifecycle on the client.
 *
 * Three behaviours, matching the server's three mechanisms:
 *
 *  1. **Proactive renewal.** A renew is scheduled before expiry and extends the session
 *     *in place, same token*. Because the playback URL does not change, hls.js,
 *     mpegts.js and `<video>` never notice — no reload, no rebuffer. This is what lets
 *     a four-hour film or an evening of live TV run past the base TTL.
 *
 *  2. **Clean re-mint.** A 410 (expired), 404 (swept, or the server restarted) or 403
 *     (hard maximum reached) mints a fresh session. Live resumes at the edge; VOD
 *     captures `currentTime` first and seeks back after the switch, so the user loses
 *     about a second rather than their place.
 *
 *  3. **Failure reporting.** When the player itself fails, `report {ok:false}` clears
 *     the item's learned format so the next play re-probes — which is how a provider
 *     silently switching a channel from HLS to TS heals itself.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { decodableAudioCodecs } from './audioSupport';
import { hasAvplay } from '../device/useDeviceProfile';
import type { StreamSessionDto } from '@filmodasi/shared';
import { api, apiUrl, ApiRequestError } from '../api/client';

/** Renew this long before expiry, or a quarter of the remaining window if that is sooner. */
const RENEW_LEAD_MS = 10 * 60_000;

export interface StreamSessionState {
  session: StreamSessionDto | null;
  error: string | null;
  loading: boolean;
  /** Bumped on every re-mint, so the player can rebuild its engine. */
  generation: number;
  /** Position to restore after a re-mint. Null for live. */
  resumeAt: number | null;
  reportFailure: (message: string) => void;
  retry: () => void;
}

export function useStreamSession(
  itemId: string | null,
  opts: { isLive: boolean; currentTime: () => number },
): StreamSessionState {
  const [session, setSession] = useState<StreamSessionDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [generation, setGeneration] = useState(0);
  const [resumeAt, setResumeAt] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentTimeRef = useRef(opts.currentTime);
  currentTimeRef.current = opts.currentTime;

  /**
   * Resolves the playback URL against the API host.
   *
   * The server returns a path, because on the web the app and the API share an origin.
   * The packaged widget does not: it runs from `file://`, where a path handed to a
   * `<video>` element resolves to the filesystem and the player fails with no network
   * request to show for it. `apiUrl` leaves a `direct_source` URL alone — that one is
   * already absolute, and it is not ours to rewrite.
   */
  const absolute = (dto: StreamSessionDto): StreamSessionDto => ({
    ...dto,
    playbackUrl: apiUrl(dto.playbackUrl),
  });

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  /** Mints a new session, optionally preserving the playback position. */
  const mint = useCallback(
    async (preservePosition: boolean): Promise<StreamSessionDto | null> => {
      if (!itemId) return null;
      // Captured before the switch: after it, the element has been reloaded and reads 0.
      const position = preservePosition && !opts.isLive ? currentTimeRef.current() : null;

      const created = await api<StreamSessionDto>('/api/stream/session', {
        method: 'POST',
        // The server cannot know what this browser decodes, and the answer varies across
        // the household — so it is told rather than guessed at.
        body: {
          itemId,
          audioCodecs: decodableAudioCodecs(),
          // Lets the server hand an undecodable stream to the panel rather than to
          // ffmpeg on the VPS.
          platformDecoder: hasAvplay(),
        },
      });

      setResumeAt(position);
      setSession(absolute(created));
      setGeneration((g) => g + 1);
      return created;
    },
    [itemId, opts.isLive],
  );

  /** Extends in place. Falls back to a re-mint on any refusal. */
  const renew = useCallback(
    async (token: string) => {
      try {
        const renewed = await api<StreamSessionDto>(`/api/stream/session/${token}/renew`, {
          method: 'POST',
        });
        // Same token, same URL — deliberately does *not* bump `generation`, so the
        // player is untouched.
        setSession(absolute(renewed));
      } catch (err) {
        const code = err instanceof ApiRequestError ? err.code : null;
        if (
          code === 'STREAM_SESSION_EXPIRED' ||
          code === 'STREAM_SESSION_HARD_EXPIRED' ||
          code === 'NOT_FOUND'
        ) {
          await mint(true).catch((e: unknown) => {
            setError(e instanceof Error ? e.message : 'Yayın yenilenemedi');
          });
          return;
        }
        // A transient network failure: leave the session alone and let the next
        // scheduled attempt try again.
        setError(err instanceof Error ? err.message : 'Yenileme başarısız');
      }
    },
    [mint],
  );

  // Initial session.
  useEffect(() => {
    if (!itemId) {
      setSession(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    mint(false)
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Yayın başlatılamadı');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `attempt` is the manual retry trigger.
  }, [itemId, attempt, mint]);

  // Renewal schedule.
  useEffect(() => {
    clearTimer();
    if (!session) return;

    const msUntilExpiry = new Date(session.expiresAt).getTime() - Date.now();
    // Whichever is sooner: a fixed lead, or a quarter of the window. The second matters
    // when the server is configured with a short TTL, where a 10-minute lead would be
    // in the past before the session even started.
    const lead = Math.min(RENEW_LEAD_MS, msUntilExpiry / 4);
    const delay = Math.max(5_000, msUntilExpiry - lead);

    timer.current = setTimeout(() => void renew(session.token), delay);
    return clearTimer;
  }, [session, renew]);

  // Release the session when the player unmounts, so its slot frees immediately rather
  // than waiting for the sweeper.
  useEffect(() => {
    return () => {
      const token = session?.token;
      if (token) void api(`/api/stream/${token}`, { method: 'DELETE' }).catch(() => {});
    };
    // Intentionally keyed on the token alone: re-running on every session object would
    // delete a session we just renewed.
  }, [session?.token]);

  const reportFailure = useCallback(
    (message: string) => {
      const token = session?.token;
      if (!token) return;
      void api(`/api/stream/${token}/report`, {
        method: 'POST',
        body: { ok: false, error: message.slice(0, 500) },
      }).catch(() => {});
    },
    [session?.token],
  );

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return { session, error, loading, generation, resumeAt, reportFailure, retry };
}
